'use client';

/**
 * FieldComparison Component
 *
 * Side-by-side field value comparison with diff highlighting.
 * Part of the identity resolution component suite.
 */

import Box from '@mui/material/Box';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import { forwardRef, useMemo } from 'react';
import { baseColors } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import type { FieldComparisonProps } from '../../types/identityResolution';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format a value for display based on field type
 */
function formatValue(value: unknown, fieldType: string): string {
  if (value === null || value === undefined) {
    return '-';
  }

  switch (fieldType) {
    case 'DATE':
    case 'DATETIME':
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }
      if (typeof value === 'string') {
        return new Date(value).toLocaleDateString();
      }
      return String(value);

    case 'BOOLEAN':
      return value ? 'Yes' : 'No';

    case 'CURRENCY':
      if (typeof value === 'number') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value);
      }
      return String(value);

    case 'NUMBER':
    case 'PERCENTAGE':
      if (typeof value === 'number') {
        return fieldType === 'PERCENTAGE' ? `${value}%` : value.toLocaleString();
      }
      return String(value);

    case 'ADDRESS':
      if (typeof value === 'object' && value !== null) {
        const addr = value as Record<string, string>;
        return [addr['street1'], addr['city'], addr['state'], addr['postalCode']].filter(Boolean).join(', ');
      }
      return String(value);

    case 'MULTI_SELECT':
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return String(value);

    default:
      return String(value);
  }
}

/**
 * Check if two values are equal
 */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || a === undefined) return b === null || b === undefined;

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  // Handle string comparison (case-insensitive)
  if (typeof a === 'string' && typeof b === 'string') {
    return a.toLowerCase().trim() === b.toLowerCase().trim();
  }

  return false;
}

/**
 * Calculate similarity between two strings (0-100)
 */
function calculateSimilarity(a: string, b: string): number {
  const strA = String(a || '')
    .toLowerCase()
    .trim();
  const strB = String(b || '')
    .toLowerCase()
    .trim();

  if (strA === strB) return 100;
  if (!strA || !strB) return 0;

  // Simple character overlap for basic similarity
  const setA = new Set(strA.split(''));
  const setB = new Set(strB.split(''));
  const intersection = new Set([...setA].filter((x) => setB.has(x)));
  const union = new Set([...setA, ...setB]);

  return Math.round((intersection.size / union.size) * 100);
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
  $isMatch: boolean;
  $highlightDifferences: boolean;
}

const StyledContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ theme, $isDark, $isMatch, $highlightDifferences }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gap: theme.spacing(2),
    alignItems: 'stretch',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    background:
      $highlightDifferences && !$isMatch
        ? $isDark
          ? 'rgba(249, 115, 22, 0.08)'
          : 'rgba(249, 115, 22, 0.06)'
        : $isDark
          ? 'rgba(255, 255, 255, 0.02)'
          : 'rgba(0, 0, 0, 0.02)',
    border: `1px solid ${
      $highlightDifferences && !$isMatch
        ? $isDark
          ? 'rgba(249, 115, 22, 0.2)'
          : 'rgba(249, 115, 22, 0.15)'
        : glass.border
    }`,
    transition: transitionPresets.all,

    '&:hover': {
      background: $isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
    },
  };
});

interface StyledValueBoxProps {
  $isDark: boolean;
  $side: 'source' | 'target';
  $isMatch: boolean;
  $highlightDifferences: boolean;
}

const StyledValueBox = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledValueBoxProps>(({ theme, $isDark, $side, $isMatch, $highlightDifferences }) => ({
  padding: theme.spacing(1.5),
  borderRadius: 6,
  background: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
  minHeight: 40,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',

  // Highlight differences
  ...($highlightDifferences &&
    !$isMatch && {
      background:
        $side === 'source'
          ? $isDark
            ? 'rgba(16, 185, 129, 0.08)'
            : 'rgba(16, 185, 129, 0.06)'
          : $isDark
            ? 'rgba(249, 115, 22, 0.08)'
            : 'rgba(249, 115, 22, 0.06)',
      border: `1px solid ${
        $side === 'source'
          ? $isDark
            ? 'rgba(16, 185, 129, 0.2)'
            : 'rgba(16, 185, 129, 0.15)'
          : $isDark
            ? 'rgba(249, 115, 22, 0.2)'
            : 'rgba(249, 115, 22, 0.15)'
      }`,
    }),

  '& .value-label': {
    fontSize: '0.6875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: 4,
    opacity: 0.7,
  },

  '& .value-text': {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    wordBreak: 'break-word',

    '&.empty': {
      color: theme.palette.text.disabled,
      fontStyle: 'italic',
    },
  },
}));

interface StyledScoreBadgeProps {
  $score: number;
  $isDark: boolean;
}

const StyledScoreBadge = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledScoreBadgeProps>(({ theme, $score }) => {
  // Color based on score
  const getColor = () => {
    if ($score >= 90) return baseColors.success[500];
    if ($score >= 70) return baseColors.warning[500];
    if ($score >= 50) return '#f97316'; // orange
    return baseColors.neutral[500];
  };

  const color = getColor();

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 48,
    padding: theme.spacing(0.5),

    '& .score-value': {
      fontSize: '0.75rem',
      fontWeight: 700,
      color,
      fontVariantNumeric: 'tabular-nums',
    },

    '& .score-label': {
      fontSize: '0.6875rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: theme.palette.text.disabled,
      marginTop: 2,
    },

    '& .match-icon': {
      width: 20,
      height: 20,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: alpha(color, 0.15),
      border: `1px solid ${alpha(color, 0.3)}`,
      color,
      fontSize: '0.75rem',
      marginBottom: 4,
    },
  };
});

const StyledFieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
}));

// ============================================================================
// Component
// ============================================================================

export const FieldComparison = forwardRef<HTMLDivElement, FieldComparisonProps>(
  ({ field, sourceValue, targetValue, score, highlightDifferences = true }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Check if values match
    const isMatch = useMemo(() => valuesEqual(sourceValue, targetValue), [sourceValue, targetValue]);

    // Calculate similarity if no score provided
    const displayScore = useMemo(() => {
      if (score !== undefined) return score;
      if (isMatch) return 100;

      const sourceStr = formatValue(sourceValue, field.fieldType);
      const targetStr = formatValue(targetValue, field.fieldType);
      return calculateSimilarity(sourceStr, targetStr);
    }, [score, isMatch, sourceValue, targetValue, field.fieldType]);

    // Format values for display
    const formattedSource = useMemo(() => formatValue(sourceValue, field.fieldType), [sourceValue, field.fieldType]);
    const formattedTarget = useMemo(() => formatValue(targetValue, field.fieldType), [targetValue, field.fieldType]);

    // Get match icon
    const getMatchIcon = () => {
      if (displayScore >= 90) return '\u2713'; // checkmark
      if (displayScore >= 70) return '\u2248'; // approximately equal
      if (displayScore >= 50) return '\u2260'; // not equal
      return '\u2212'; // minus
    };

    return (
      <Box ref={ref}>
        <StyledFieldLabel>{field.label}</StyledFieldLabel>

        <StyledContainer
          $isDark={isDark}
          $isMatch={isMatch}
          $highlightDifferences={highlightDifferences}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Source Value */}
          <StyledValueBox
            $isDark={isDark}
            $side="source"
            $isMatch={isMatch}
            $highlightDifferences={highlightDifferences}
          >
            <span className="value-label">Source</span>
            <span className={`value-text ${formattedSource === '-' ? 'empty' : ''}`}>{formattedSource}</span>
          </StyledValueBox>

          {/* Score Badge */}
          <GlassTooltip title={isMatch ? 'Exact match' : `${displayScore}% similar`} arrow>
            <StyledScoreBadge $score={displayScore} $isDark={isDark}>
              <span className="match-icon">{getMatchIcon()}</span>
              <span className="score-value">{displayScore}%</span>
            </StyledScoreBadge>
          </GlassTooltip>

          {/* Target Value */}
          <StyledValueBox
            $isDark={isDark}
            $side="target"
            $isMatch={isMatch}
            $highlightDifferences={highlightDifferences}
          >
            <span className="value-label">Match</span>
            <span className={`value-text ${formattedTarget === '-' ? 'empty' : ''}`}>{formattedTarget}</span>
          </StyledValueBox>
        </StyledContainer>
      </Box>
    );
  },
);

FieldComparison.displayName = 'FieldComparison';

export default FieldComparison;
